import getters from './getters';
import mutations from './mutations';
import actions from './actions';

const state = {
  /**
   * Mapping for notifications
   * @property{string} text, equals the notificationCategoryDTO name
   * @property{string} value, equals the notificationCategoryDTO value
   * @property{string} notificationTitleTranslation, the translation for notification title (if applicable)
   * @property{string} notificationTextBeforeTranslation, the translation for notification description first part
   * @property{string} notificationTextAfterTranslation, the translation for notification description second part (if applicable)
   * @property{string} notificationButtonTranslation, the translation for notification 'check it' button
   * @property{string} showModal, the modal that will be shown on 'check it' button click
   * @property{boolean} dynamicText, if the notification description contains date (or other dynamic) info
   * WAG-65 jira ticket
   */
  phoneShortcutEnabled: false,
  homeScreenModalDisable: false,
  installPromptEvent: null,
  pointsPerQuestion: 0,
  availableFBLanguagesForSDK: Object.freeze({
    EN: 'en_US',
    AR: 'ar_AR',
    FA: 'fa_IR',
    FR: 'fr_FR',
  }),
  addOnMode: null,
  addOnModesEnum: Object.freeze({
    DOUBLE_POINTS_OR_LOSE: {
      text: 'DOUBLE_POINTS_OR_LOSE',
      hide: false,
      assignPointsAfterAddOnGame: true,
      factor: 2,
      lostPointsForMissedAddOnGame: true,
    },
    TRIPLE_POINTS_OR_LOSE: {
      text: 'TRIPLE_POINTS_OR_LOSE',
      hide: false,
      assignPointsAfterAddOnGame: true,
      factor: 3,
      lostPointsForMissedAddOnGame: true,
    },
    ONLY_DOUBLE: {
      text: 'ONLY_DOUBLE',
      hide: false,
      assignPointsAfterAddOnGame: true,
      factor: 2,
      lostPointsForMissedAddOnGame: false,
    },
    ONLY_TRIPLE: {
      text: 'ONLY_TRIPLE',
      hide: false,
      assignPointsAfterAddOnGame: true,
      factor: 3,
      lostPointsForMissedAddOnGame: false,
    },
    NO_WIN_LOSE: {
      text: 'NO_WIN_LOSE',
      hide: false,
      assignPointsAfterAddOnGame: false,
    },
    HIDE: {
      text: 'HIDE',
      hide: true,
    },
  }),
  hideExchangeStars: true,
  exchangeStarsPoints: 0,
  howItWinType: 0,
  notificationsNames: Object.freeze({
    ChangeUsernameOrPin: {
      text: 'Change Username or Pin',
      value: '',
      notificationTitleTranslation: 'home_notifications_change_username_or_pin_title',
      notificationTextBeforeTranslation: '',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: 'home_notifications_change_username_or_pin_button',
      showModal: '',
      dynamicText: false,
      notificationText: false,
    },
    BonusPhasesDaily: {
      text: 'Bonus Phases Daily',
      value: '',
      notificationTitleTranslation: 'home_notification_bonus_phase_daily_bonus_title',
      notificationTextBeforeTranslation: '',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: 'home_notifications_check_it',
      showModal: '',
      dynamicText: false,
      notificationText: false,
    },
    BonusRegistration: {
      text: 'Bonus Points',
      value: 'Registration',
      notificationTitleTranslation: 'home_modals_notifications_bonus_registration',
      notificationTextBeforeTranslation: 'notifications_bonus_registration_before',
      notificationTextAfterTranslation: 'notifications_bonus_registration_after',
      notificationButtonTranslation: '',
      showModal: '',
      dynamicText: false,
      notificationText: false,
    },
    BundleDiscount: {
      text: 'Bundle Discount',
      value: 'Bundle Discount',
      notificationTitleTranslation: 'notifications_bundle_discount_title',
      notificationTextBeforeTranslation: 'notifications_bundle_discount_before',
      notificationTextAfterTranslation: 'notifications_bundle_discount_after',
      notificationButtonTranslation: 'home_notifications_check_it',
      showModal: '',
      dynamicText: true,
      notificationText: false,
    },
    BonusPointsPhase: {
      text: 'Bonus Points Phase',
      value: 'Bonus Points Phase',
      notificationTitleTranslation: 'notifications_bonus_points_phase_title',
      notificationTextBeforeTranslation: 'notifications_bonus_points_phase_before',
      notificationTextAfterTranslation: 'notifications_bonus_points_phase_after',
      notificationButtonTranslation: 'home_notifications_check_it',
      showModal: '',
      dynamicText: true,
      notificationText: false,
    },
    Achievement: {
      text: 'Achievement',
      value: 'Achievement',
      notificationTitleTranslation: 'home_notifications_achievement_title',
      notificationTextBeforeTranslation: 'notifications_achievement_text_before',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: 'home_notifications_check_it',
      showModal: 'NewAchievement',
      dynamicText: false,
      notificationText: false,
    },
    SpinWheel: {
      text: 'Pending spinning wheel game',
      value: 'PendingSpinningWheelGame',
      notificationTitleTranslation: 'home_notification_spin_wheel_title',
      notificationTextBeforeTranslation: 'notifications_spin_wheel_text_before',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: 'home_notifications_check_it',
      showModal: 'SpinWheel',
      dynamicText: false,
      notificationText: false,
    },
    InvitationRegistration: {
      text: 'Free Game',
      value: 'Referral',
      notificationTitleTranslation: 'notifications_invite_registration_title',
      notificationTextBeforeTranslation: 'notifications_invite_registration_before',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: '',
      showModal: '',
      dynamicText: false,
      notificationText: true,
    },
    TopRanking: {
      text: 'Top Ranking',
      value: 'TopRanking',
      notificationTitleTranslation: 'notifications_invite_ranking_title',
      notificationTextBeforeTranslation: 'notifications_ranking_before',
      notificationTextAfterTranslation: '',
      notificationButtonTranslation: '',
      showModal: 'Ranking',
      dynamicText: false,
    },
  }),
  activeModals: [],
  inviteConfig: {},
  optaEnabled: null,
  isWinnersMsisdnEnabled: false,
  oneTimeSubscriptionBundleId: null,
  defaultDailySubButtonEnabled: true,
  minGamesToPlay: 0,
  cpc: null,
  showMsisdnInProfilePage: false,
  hideChangePassword: false,
  hideLangSelection: false,
  timeWinnerIndication: null,
  hideLeaderboard: false,
  purchaseBundleFromUnsubUser: false,
  integrationHeader: '',
  simpleRegistration: '',
  showSelectOperatorModal: false,
  hasSingleOptIn: false,
  availableIntegrations: {
    KSA_MOBILY: {
      name: 'ksa_mobily_he',
      info: [{ key: 'operator', value: '' }, { key: 'msisdn', value: '' }, { key: 'lang', value: '' }],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: true,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: true,
      hasHeReSub: true,
      resubOnForgot: false,
      unsubWithSms: true,
    },
    JAWWAL_PALESTINE: {
      name: 'jawwal_pal_he',
      info: [],
      transferDigitalParams: false,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: true,
      disableUpdateLangInCore: true,
      mapLang: [],
      hasAsync: true,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    TPAY_EGYPT: {
      name: 'tpay_egypt_he',
      info: [{ key: 'operator', value: '' }, { key: 'msisdn', value: '' }, { key: 'lang', value: '' }],
      transferDigitalParams: false,
      affectsMultiOperator: true,
      storeDigitalParams: true,
      hasExternalRedirection: false,
      hasThirdPartyReSub: true,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: true,
      resubOnForgot: true,
      unsubWithSms: true,
    },
    KOREK_IRAQ: {
      name: 'koreq_iraq_he',
      info: [],
      transferDigitalParams: false,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: true,
      disableUpdateLangInCore: true,
      mapLang: [{ key: 'KU', value: 'KD' }],
      hasAsync: true,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    AFRICELL_DRC: {
      name: 'africell_drc_he',
      info: [],
      transferDigitalParams: false,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: true,
      disableUpdateLangInCore: true,
      mapLang: [],
      hasAsync: true,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    ASIACELL_IRAQ: {
      name: 'asiacell_iraq_he',
      info: [],
      transferDigitalParams: false,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: true,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    ORANGE_EGYPT: {
      name: 'orange_egypt_he',
      info: [],
      transferDigitalParams: false,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    VIVA_KUWAIT: {
      name: 'viva_kuwait_he',
      info: [{ key: 'lang', value: '' }],
      transferDigitalParams: true,
      storeDigitalParams: false,
      affectsMultiOperator: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    VODACOM: {
      name: 'vodacom_he',
      info: [{ key: 'packageId', value: '' }],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: false,
      hasExternalRedirection: true,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    DU_ETISALAT: {
      name: 'du_etisalat',
      info: [{ key: 'operator', value: '' }, { key: 'msisdn', value: '' }, { key: 'lang', value: '' }],
      transferDigitalParams: false,
      affectsMultiOperator: true,
      storeDigitalParams: true,
      hasExternalRedirection: false,
      hasThirdPartyReSub: true,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    VIVA_BAHRAIN: {
      name: 'viva_bahrain_he',
      info: [],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: true,
      hasConfirmation: true,
      disableLogout: true,
      hideTerms: true,
      oneTimeSubscription: false,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    OOREDOO_OMAN: {
      name: 'ooredoo_oman_he',
      info: [],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: true,
      enabledStatusInSettingsPage: false,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    OMANTEL_OMAN: {
      name: 'omantel_oman_he',
      info: [],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hideTerms: false,
      oneTimeSubscription: true,
      enabledStatusInSettingsPage: true,
      hideLangSelectionLoginArea: false,
      disableUpdateLangInCore: false,
      mapLang: [],
      hasAsync: false,
      hasHeReSub: false,
      resubOnForgot: false,
      unsubWithSms: false,
    },
    OOREDOO_QATAR: {
      name: 'ooredoo_qatar_he',
      info: [{ key: 'msisdn', value: '' }],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: false,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hasAuto: false,
      hasForgot: true,
      hasServerTrack: false,
      hasDoubleOnNetFlow: false,
    },
    GUINEA_MTN: {
      name: 'guinea_mtn_he',
      info: [],
      transferDigitalParams: false,
      affectsMultiOperator: false,
      storeDigitalParams: true,
      hasExternalRedirection: false,
      hasThirdPartyReSub: false,
      hidePage: false,
      hasConfirmation: false,
      disableLogout: false,
      hasAsync: false,
      hasOptOutAsync: false,
      showRegistrationModal: false,
    },
  },
  availableFlows: {
    DEFAULT: {
      propertyName: 'DEFAULT',
      blockedRoutes: [],
    },
    SIMPLE_WITH_WEB_REGISTRATION: {
      propertyName: 'SIMPLE_WITH_WEB_REGISTRATION',
      blockedRoutes: ['/forgotPassword2', '/forgotPassword3'],
    },
    SIMPLE_WITHOUT_WEB_REGISTRATION: {
      propertyName: 'SIMPLE_WITHOUT_WEB_REGISTRATION',
      blockedRoutes: ['/twostepregister1', '/forgotPassword2', '/forgotPassword3', '/twostepforgotPassword1'],
    },
  },
  mergedParam: '',
  hidePrizePage: false,
  purchaseViaSMS: null,
  fbAppId: '',
  versionInfo: {},
  roundTimeList: [],
  multiOperatorInfo: {
    multiOperatorEnabled: false,
    multiOperatorOrdinal: 0,
    multiOperatorDisplayNames: [],
    operatorPicked: '',
    multiOperatorKeys: [],
    multiOperatorDisplayedNamesInRegisterPage: [],
  },
  freeTrialGuaranteedPrize: null,
  freeTrialPoints: null,
  freeTrialRegistrationPoints: null,
  freeTrialRedirectionUrl: null,
  freeTrialUserPrice: null,
  winnersShowMsisdn: false,
  duelSkin: 0,
  displayPrice: false,
  hideStore: false,
  smsAutomaticResponse: false,
  hideWinners: false,
  facebookFunctionality: false,
  lastRandomRound: 0,
  banner: '',
  userAvatar: '',
  fbReferral: '',
  connectionPoints: 0,
  reoptinOnestep: false,
  refUrl: '',
  correlationId: '',
  campaignId: '',
  publisherId: '',
  clickid: '',
  splashUrl: '',
  splashTimer: 0,
  profilePath: true,
  modalForHomePage: null,
  modalForSettings: null,
  modalForStore: null,
  avatarGrouping: 0,
  storeItems: [],
  pinForBundles: false,
  subAndUnsubViaSMS: false,
  showOptOutPhase: false,
  reoptinAndOptoutConfig: '',
  twoStepRegistration: false,
  freeTrialStripe: false,
  logoOnFooter: false,
  trackingId: '',
  facebookPixelId: '',
  vatBanner: false,
  soundsOn: true,
  userDetails: {
    displayAvatar: false,
    msisdn: '',
    level: 0,
    totalPointsEarned: 0,
    dailyPointsEarned: 0,
    weeklyPointsEarned: 0,
    monthlyPointsEarned: 0,
    gamesPlayed: 0,
    gamesWon: 0,
    answersGiven: 0,
    correctAnswersGiven: 0,
    availableGames: 0,
    freeGamesUntil: 0,
    language: '',
    gamesToNextLevel: 0,
    username: '',
    avatar: '',
    avatarGender: 'male',
    subscribed: '',
    facebookId: '',
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/common/index.js